import React from 'react';
import AsyncSelect from 'react-select/async';
import Radium from 'radium';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { components } from 'react-select';
import isEqual from 'lodash.isequal';

const styles = {
	customlabel: {
		fontSize: '0.8em',
		marginTop: 5,
		marginLeft: 2,
	},
	crossRequired: {
		color: '#c5190e',
		fontSize: '1em',
		position: 'relative',
		bottom: 1,
		left: 3,
	},
};
const errorStyle = {
	marginBottom: 10,
	color: '#dc0000',
	fontSize: '0.7em',
};

const selectCustomErrorStyles = {
	container: (provided) => ({
		...provided,
		cursor: 'pointer',
	}),
	option: (provided) => ({
		...provided,
		color: 'rgba(0, 0, 0, 0.87)',
		cursor: 'pointer',
	}),
	control: (provided) => ({
		...provided,
		background: '#fff6f6',
		borderColor: '#e0b4b4',
		color: '#9f3a38',
		cursor: 'pointer',
	}),
};

const selectCustomStyles = {
	container: (provided) => ({
		...provided,
		cursor: 'pointer',
	}),
	option: (provided) => ({
		...provided,
		cursor: 'pointer',
	}),
	control: (provided) => ({
		...provided,
		cursor: 'pointer',
	}),
};

// Used to disable google autoComplete
const Input = ({ autoComplete, ...props }) => {
	return <components.Input {...props} autoComplete='off' />;
};

const Option = (props) => {
	const width = 100 / props.data.labels?.length;
	return (
		<components.Option {...props}>
			{props.data.labels && (
				<div style={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
					{props.data.labels.map((label) => (
						<div key={`option-${props.data.key}-${label.text}`}
							style={{
								display: 'flex',
								width: `${label.width ?? width}%`,
								textAlign: 'left',
								fontSize: 12,
								wordBreak: 'break-word',
							}}
						>
							{label.text}
						</div>
					))}
				</div>
			)}
			{!props.data.labels && props.data.label}
		</components.Option>
	);
};

const AsyncFilterSelect = ({
	form: { errors, touched, setFieldValue },
	field: { name, onBlur, value },
	customlabel = '',
	required = false,
	promiseOptions,
	defaultValue,
	placeholder,
	isCreatable = false,
	onCreateClick,
	createdValue,
	customOnChange = null,
	onInputChange = null,
	id,
	defaultOptions = null,
	disabled,
	isLoading,
	isClearable = false,
	onClear = null,
}) => {
	id = id ?? name;
	let selectRef = null;

	if (
		createdValue &&
		!isEqual(value, createdValue) &&
		(createdValue.attribute === null || createdValue.attribute === name)
	)
		setFieldValue(name, createdValue);

	const getValue = () => {
		if (value && value.id) return value;
		else if (value && typeof value === 'string')
			return { value: value, label: value };
		else return null;
	};

	return (
		<div>
			<div style={styles.customlabel}>
				{customlabel}
				{required && <span style={styles.crossRequired}>*</span>}
			</div>

			{isCreatable && (
				<AsyncCreatableSelect
					ref={(ref) => {
						selectRef = ref;
					}}
					name={name}
					inputId={id}
					defaultValue={defaultValue}
					value={getValue()}
					loadOptions={promiseOptions}
					onChange={(option, actionType) => {
						if (isClearable && actionType.action === 'clear' && onClear)
							onClear();
						else if (customOnChange) customOnChange(option);
						else setFieldValue(name, option);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && (isLoading || selectRef?.state?.loadedOptions?.length === 0)) {
							e.preventDefault();
						}
					}}
					onInputChange={(newValue) => {
						const oldValue = selectRef?.state?.inputValue;
						if (onInputChange) return onInputChange(newValue, oldValue);
						else return newValue;
					}}
					onBlur={onBlur}
					onCreateOption={onCreateClick}
					placeholder={
						placeholder || 'Sélectionner une valeur ou taper votre réponse'
					}
					styles={
						touched[id] && errors[name]
							? selectCustomErrorStyles
							: selectCustomStyles
					}
					components={{ Option, Input }}
					defaultOptions={defaultOptions}
					isDisabled={disabled}
					formatCreateLabel={(inputValue) => `Créer "${inputValue}"`}
					noOptionsMessage={() => 'Aucun résultat'}
					loadingMessage={() => 'Chargement...'}
					isClearable={isClearable}
				/>
			)}

			{!isCreatable && (
				<AsyncSelect
					ref={(ref) => {
						selectRef = ref;
					}}
					name={name}
					inputId={String(id)}
					defaultValue={defaultValue}
					loadOptions={promiseOptions}
					onChange={(option, actionType) => {
						if (isClearable && actionType.action === 'clear' && onClear)
							onClear();
						else if (customOnChange) customOnChange(option);
						else setFieldValue(name, option);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && (isLoading || selectRef?.state?.loadedOptions?.length === 0)) {
							e.preventDefault();
						}
					}}
					onInputChange={(newValue) => {
						const oldValue = selectRef?.state?.inputValue;
						if (onInputChange) return onInputChange(newValue, oldValue);
						else return newValue;
					}}
					onBlur={onBlur}
					placeholder={placeholder || 'Sélectionner une valeur dans la liste'}
					styles={touched[id] && errors[name] ? selectCustomErrorStyles : {}}
					components={{ Option, Input }}
					defaultOptions={defaultOptions}
					isDisabled={disabled}
					noOptionsMessage={() => 'Aucun résultat'}
					loadingMessage={() => 'Chargement...'}
					value={getValue()}
					isClearable={isClearable}
				/>
			)}
			{touched[name] && errors[name] && (
				<div style={errorStyle}>{errors[name]?.id || errors[name]}</div>
			)}
		</div>
	);
};

export default Radium(AsyncFilterSelect);
